import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';

import { GridApi, GridOptions } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { getCurrentUser } from 'app/core/store/currentUser/user.selectors';

import { PlanEntry } from '../add-plan-dialog/add-plan-dialog.component';
import { TimeEntryForPlanningService } from '../time-entry-for-planning.service';
import { ConsultantDetailsService } from '../../../common/services/consultant-details.service';

@Component({
  selector: 'app-planning-entry-table',
  templateUrl: './planning-entry-table.component.html',
  styleUrls: ['./planning-entry-table.component.scss']
})
export class PlanningEntryTableComponent
  implements OnInit, OnChanges, OnDestroy {

  @Input() entries: PlanEntry[] = [];
  @Output() selectionChanged = new EventEmitter<number[]>();
  @Output() entriesChanged = new EventEmitter<PlanEntry[]>();
  @Output() editEntryRequested = new EventEmitter<PlanEntry>();
  @Output() filteredEntriesChanged = new EventEmitter<PlanEntry[]>();
  @Input() loggedInUsername = '';

  gridApi: GridApi;
  gridOptions: GridOptions = {};

  columnDefs: any[] = [];
  autoGroupColumnDef: any = {};

  getRowId = (params: any) => String(params.data.id);

  filteredEntries: PlanEntry[] = [];

  private showClashesOnly = false;
  private showNonClashedOnly = false;

  private clashDates = new Set<string>();

  private chargedFilter: 'All' | 'Charged' | 'Uncharged' = 'All';

  private clientFilter: string[] = [];
  private projectFilter: string[] = [];
  private employeeFilter: string[] = [];

  private dateRangeFilter: {
    start: string | null;
    end: string | null;
  } = {
    start: null,
    end: null
  };

  private currentUserFirstName = '';
  private employees: any[] = [];
  private currentUser: any;

  private subscriptions = new Subscription();

  // Currency codes are stored uppercased, so lookups are always upper-cased too
  private readonly currencySymbolMap: { [code: string]: string } = {
    GBP: '£',
    USD: '$',
    SGD: 'S$',
    INR: 'R',
  };

  private getCurrencySymbol(code: string | null | undefined): string {
    if (!code) {
      return '';
    }
    const upper = code.toUpperCase();
    return this.currencySymbolMap[upper] ?? upper;
  }

  constructor(
    private planningService: TimeEntryForPlanningService,
    private store: Store,
    private consultantDetailsService: ConsultantDetailsService
  ) {}

  
private canModifyEntry(entry: PlanEntry): boolean {
  if (!entry) {
    return false;
  }

  // Allow editing of new, unsaved entries.
  if (entry.isUnsaved === true) {
    return true;
  }

  // Placeholders are not actual saved entries.
  if (entry.isPlaceholder) {
    return false;
  }

  const ownerLogin = entry.entryOwnerLoginName?.trim().toLowerCase();
  const loggedInLogin = this.loggedInUsername?.trim().toLowerCase();

  // Fail closed if owner or logged-in user information is missing.
  if (!ownerLogin || !loggedInLogin) {
    return false;
  }

  // Users can always modify their own entries.
  if (ownerLogin === loggedInLogin) {
    return true;
  }

  return false;
}

  // ----- Clash key helper -----

  private clashKey(consultant: string, date: string): string {
    return this.planningService.clashKey(consultant, date);
  }
  private updateCurrentUserFirstName(): void {
  const employeeCode = this.currentUser?.Employee;

  if (employeeCode && this.employees.length > 0) {
    const matched = this.employees.find(
      e => e.Employee?.trim().toLowerCase() === employeeCode.trim().toLowerCase()
    );

    this.currentUserFirstName = matched
      ? matched.FirstName?.trim() ?? ''
      : (this.currentUser?.FirstName?.trim() ?? '');
  } else {
    this.currentUserFirstName = this.currentUser?.FirstName?.trim() ?? '';
  }

  if (this.gridApi) {
    this.gridApi.refreshCells({
      force: true
    });
    this.gridApi.redrawRows();
  }
}
  // ----- Lifecycle hooks -----

  ngOnInit(): void {
      this.subscriptions.add(
      this.store.select(getCurrentUser).subscribe((user) => {
        this.currentUser = user;
        this.updateCurrentUserFirstName();
      })
    );
    this.subscriptions.add(
        this.consultantDetailsService.getConsultantDetails().subscribe((consultants: any[]) => {
        this.employees = consultants.map(c => ({
          ...c,
          FullName: `${c.FirstName} ${c.LastName}`.trim()
        }));

        this.updateCurrentUserFirstName();
      })
    );

    this.subscriptions.add(
      this.planningService.showClashes$.subscribe(value => {
        this.showClashesOnly = value;
        this.applyFilters();
      })
    );

    this.subscriptions.add(
      this.planningService.showNonClashed$.subscribe(value => {
        this.showNonClashedOnly = value;
        this.applyFilters();
      })
    );

    // ----- Grid options -----

    this.gridOptions.defaultColDef = {
      resizable: true
    };

    this.gridOptions.groupDefaultExpanded = 1;
    this.gridOptions.groupSelectsChildren = true;
    this.gridOptions.suppressAggFuncInHeader = true;

    this.gridOptions.isRowSelectable = (rowNode) =>
      !rowNode.group &&
      this.canModifyEntry(rowNode.data) &&
      !rowNode.data?.isPlaceholder;

    // ----- Row styling -----

    this.gridOptions.getRowStyle = (params) => {
      // Group rows
      if (params.node.group) {
        return {
          background: '#6ac738',
          'font-weight': 'bold'
        };
      }

      // Locked entries (not owned by the current user)
      if (params.data && !this.canModifyEntry(params.data)) {
        return {
          color: '#9ca3af'
        };
      }

      return null;
    };

    // ----- Row class rules (unsaved / clash highlighting) -----

    this.gridOptions.rowClassRules = {
      // Orange = saved clash-causing entry
      'planning-entry-clash': (params: any) => {
        return (
          !params.node.group &&
          !params.data?.isPlaceholder &&
          params.data?.isUnsaved !== true &&
          params.data?.isClashEntry === true
        );
      },

      // Yellow = unsaved entry
      'planning-entry-unsaved': (params: any) => {
        return (
          !params.node.group &&
          !params.data?.isPlaceholder &&
          params.data?.isUnsaved === true
        );
      }
    };

    // ----- Columns -----

    this.columnDefs = [

      // Checkbox / lock icon column
      {
        headerName: '',
        field: 'task',
        width: 60,
        pinned: 'left',

        checkboxSelection: (params: any) =>
          params.node.group ||
          this.canModifyEntry(params.data),

        headerCheckboxSelection: true,

        suppressMenu: true,
        sortable: false,
        filter: false,

        // Shows a lock icon for entries the current user doesn't own
        cellRenderer: (params: any) => {
          if (params.node.group) {
            return '';
          }

          if (
            params.data &&
            !this.canModifyEntry(params.data)
          ) {
            return `
              <span
                title="Locked - not your entry"
                style="
                  display:inline-flex;
                  align-items:center;
                  justify-content:center;
                  width:100%;
                  height:100%;
                "
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#9ca3af"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <rect
                    x="3"
                    y="11"
                    width="18"
                    height="11"
                    rx="2"
                  ></rect>

                  <path
                    d="M7 11V7a5 5 0 0 1 10 0v4"
                  ></path>
                </svg>
              </span>
            `;
          }

          return '';
        },

        valueGetter: () => ''
      },

      // Clash indicator column
      {
        headerName: '',
        field: 'clashIndicator',
        width: 40,
        pinned: 'left',

        suppressMenu: true,
        sortable: false,
        filter: false,

        // Shows a warning icon when this consultant/date combination clashes
        cellRenderer: (params: any) => {
          if (params.node.group) {
            return '';
          }

          if (params.data?.isPlaceholder) {
            return '';
          }

          if (
            params.data &&
            this.clashDates.has(
              this.clashKey(params.data.consultant, params.data.date)
            )
          ) {
            return `
              <span
                title="Clashing entry"
                style="
                  display:inline-flex;
                  align-items:center;
                  justify-content:center;
                  width:100%;
                  height:100%;
                "
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f59e0b"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                  ></circle>

                  <line
                    x1="12"
                    y1="8"
                    x2="12"
                    y2="12"
                  ></line>

                  <line
                    x1="12"
                    y1="16"
                    x2="12.01"
                    y2="16"
                  ></line>
                </svg>
              </span>
            `;
          }

          return '';
        },

        valueGetter: () => ''
      },

      // Client
      {
        headerName: 'Client',
        field: 'client',
        width: 110,

        valueFormatter: params =>
          params.node.group
            ? ''
            : this.formatOrDash(params.value)
      },

      // Project
      {
        headerName: 'Project',
        field: 'project',
        width: 120,

        valueFormatter: params =>
          params.node.group
            ? ''
            : this.formatOrDash(params.value)
      },

      // Task ref.
      {
        headerName: 'Task Ref.',
        field: 'taskRef',
        width: 110,

        valueFormatter: params =>
          params.node.group
            ? ''
            : this.formatOrDash(params.value)
      },

      // Hours
      {
        headerName: 'Hours',
        field: 'hours',
        width: 80,

        aggFunc: 'sum',

        valueGetter: params =>
          params.node.group
            ? undefined
            : Number(params.data.hours)
      },

      // Rate
      {
        headerName: 'Rate',
        field: 'rate',
        width: 90,

        filter: 'agNumberColumnFilter',

        valueFormatter: params => {
          if (params.node.group || params.data?.isPlaceholder) {
            return '';
          }

          const symbol = this.getCurrencySymbol(params.data?.currencyCode);

          return `${symbol}${Number(params.value || 0).toFixed(2)}`;
        }
      },

      // Consultant
      {
        headerName: 'Consultant',
        field: 'consultant',
        width: 145,

        valueFormatter: params =>
          params.node.group
            ? ''
            : this.formatOrDash(params.value)
      },

      // Entry owner
      {
        headerName: 'Entry Owner',
        field: 'entryOwner',
        width: 145,

        valueFormatter: params =>
          params.node.group
            ? ''
            : this.formatOrDash(params.value)
      },

      // Charge
      {
        headerName: 'Charge',
        field: 'charge',
        width: 92,

        valueFormatter: params => {
          if (params.node.group) {
            return '';
          }

          if (params.data?.isPlaceholder) {
            return '';
          }

          return this.formatOrDash(params.value);
        }
      },

      // Entry date (grouping column, hidden as its own column)
      {
        headerName: 'Entry Date',
        field: 'date',

        rowGroup: true,
        hide: true,
        sort: 'asc',

        valueFormatter: params =>
          this.formatDate(params.value),

        showRowGroup: true,
      }

    ];

    // ----- Group column -----

    this.autoGroupColumnDef = {
      headerName: 'Task',
      width: 300,
      field: 'task',
      resizable: true
    };

    // ----- Filter subscriptions -----
    this.subscriptions.add(
      this.planningService.chargedFilter$.subscribe(value => {
        this.chargedFilter = value;
        this.applyFilters();
      })
    );

    this.subscriptions.add(
      this.planningService.clientFilter$.subscribe(value => {
        this.clientFilter = value;
        this.applyFilters();
      })
    );

    this.subscriptions.add(
      this.planningService.projectFilter$.subscribe(value => {
        this.projectFilter = value;
        this.applyFilters();
      })
    );

    this.subscriptions.add(
      this.planningService.employeeFilter$.subscribe(value => {
        this.employeeFilter = value;
        this.applyFilters();
      })
    );

    this.subscriptions.add(
      this.planningService.dateRange$.subscribe(value => {
        this.dateRangeFilter = value;
        this.applyFilters();
      })
    );

    this.applyFilters();
  }

  // On changes - re-applies filters (and redraws the grid) when the parent
  // passes in a new set of entries
  
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['entries'] ||
      changes['loggedInUsername']
    ) {
      this.applyFilters();

      if (this.gridApi) {
        this.gridApi.redrawRows();
        this.gridApi.refreshCells({ force: true });
      }
    }
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // ----- Date range placeholders -----

  // Fills any date in [start, end] that has no entry with a placeholder row,
  // so the grid always shows a full run of dates even on empty days
  private fillDateRangeGaps(
    entries: PlanEntry[],
    start: string,
    end: string
  ): PlanEntry[] {
    const existingDates = new Set(
      entries.map(e => e.date)
    );

    const placeholders: PlanEntry[] = [];

    const cursor = new Date(`${start}T00:00:00`);
    const endDate = new Date(`${end}T00:00:00`);

    let placeholderId = -1;

    while (cursor <= endDate) {
      const iso = this.toISODate(cursor);

      if (!existingDates.has(iso)) {
        placeholders.push({
          id: placeholderId--,
          date: iso,

          client: '',
          consultant: '',
          entryOwner: '',
          company: '',
          project: '',
          taskRef: '',
          task: '',

          hours: 0,
          rate: 0,
          allocation: '',

          charge: 'No',

          invoiceDate: iso,

          isPlaceholder: true,

          seriesId: null
        });
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    return [
      ...entries,
      ...placeholders
    ];
  }

  // Formats a Date as YYYY-MM-DD using local date parts
  private toISODate(date: Date): string {
    const year = date.getFullYear();

    const month = (date.getMonth() + 1)
      .toString()
      .padStart(2, '0');

    const day = date.getDate()
      .toString()
      .padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // ----- Formatting -----

  private formatOrDash(value: any): string {
    if (
      value === '' ||
      value === null ||
      value === undefined
    ) {
      return '-';
    }

    return value;
  }

  // ----- Filtering -----

  // No filter selected = no entries shown. Selecting a filter narrows by
  // that criteria only. Unsaved entries always shown regardless. Unsaved entries are
  // always kept regardless of filters.
  private applyFilters(): void {
    const noFiltersSelected =
      this.clientFilter.length === 0 &&
      this.projectFilter.length === 0 &&
      this.employeeFilter.length === 0 ;

    if (noFiltersSelected) {
      this.filteredEntries = this.entries.filter(entry => entry.isUnsaved === true);
      Promise.resolve().then(() => this.filteredEntriesChanged.emit(this.filteredEntries));

      if (this.gridApi) {
        this.gridApi.refreshCells({ columns: ['clashIndicator'], force: true });
        this.gridApi.redrawRows();
      }
      return;
    }

    let result = this.entries;

    this.clashDates = this.planningService.getClashKeysFromEntries(this.entries);

    if (this.clientFilter.length > 0) {
      result = result.filter(
        entry => entry.isUnsaved === true || this.clientFilter.includes(entry.client)
      );
    }

    if (this.projectFilter.length > 0) {
      result = result.filter(
        entry => entry.isUnsaved === true || this.projectFilter.includes(entry.project)
      );
    }

    if (this.employeeFilter.length > 0) {
      result = result.filter(
        entry =>
          entry.isUnsaved === true ||
          this.employeeFilter.some(
            selected =>
              selected.trim().toUpperCase() ===
              (entry.consultant || '').trim().toUpperCase()
          )
      );
    }

    if (this.dateRangeFilter.start) {
      result = result.filter(
        entry => entry.isUnsaved === true || entry.date >= this.dateRangeFilter.start!
      );
    }

    if (this.dateRangeFilter.end) {
      result = result.filter(
        entry => entry.isUnsaved === true || entry.date <= this.dateRangeFilter.end!
      );
    }


  if (this.showClashesOnly && this.showNonClashedOnly) {
  // Both checked — no restriction, keep everything
  } else if (this.showClashesOnly) {
    result = result.filter(
      entry =>
        !entry.isPlaceholder &&
        this.clashDates.has(this.clashKey(entry.consultant, entry.date))
    );
  } else if (this.showNonClashedOnly) {
    result = result.filter(
      entry =>
        !entry.isPlaceholder &&
        !this.clashDates.has(this.clashKey(entry.consultant, entry.date))
    );
  } else {
    // Neither checked — show nothing
    result = [];
  }

  if (this.chargedFilter === 'Charged') {
    result = result.filter(entry => entry.isUnsaved === true || entry.charge === 'Yes');
  } else if (this.chargedFilter === 'Uncharged') {
    result = result.filter(entry => entry.isUnsaved === true || entry.charge === 'No');
  }

  const hasRealEntries = result.some(entry => !entry.isPlaceholder);

  if (
    this.dateRangeFilter.start &&
    this.dateRangeFilter.end &&
    this.showClashesOnly &&
    this.showNonClashedOnly &&
    hasRealEntries
  ) {
    result = this.fillDateRangeGaps(
      result,
      this.dateRangeFilter.start,
      this.dateRangeFilter.end
    );
  }
  this.filteredEntries = result;
  Promise.resolve().then(() => this.filteredEntriesChanged.emit(result));

  if (this.gridApi) {
    this.gridApi.refreshCells({
      columns: ['clashIndicator'],
      force: true
    });

    this.gridApi.redrawRows();
  }
}

  // ----- Grid ready -----

  onGridReady(params: { api: GridApi }): void {
    this.gridApi = params.api;

    // Apply current row classes immediately after grid initialization.
    this.gridApi.redrawRows();
  }

  // ----- Selection -----

  onRowSelection(): void {
    this.selectionChanged.emit(
      this.gridApi
        .getSelectedRows()
        .map(entry => entry.id)
    );
  }

  // ----- Edit -----

  // Opens the edit dialog on double-click, unless the row is a group row,
  // a placeholder, or an entry the current user doesn't own
  onCellDoubleClicked(params: any): void {
    if (params.node.group) {
      return;
    }

    if (params.data?.isPlaceholder) {
      return;
    }

    if (!this.canModifyEntry(params.data)) {
      return;
    }

    this.editEntryRequested.emit(
      params.data as PlanEntry
    );
  }

  // ----- Date format -----

  // Formats an ISO date as "DD Mon YYYY, Weekday" for the grid's group headers
  private formatDate(date: string): string {
    if (!date) {
      return '';
    }

    const formatted =
      new Intl.DateTimeFormat(
        'en-GB',
        {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          weekday: 'long'
        }
      ).format(
        new Date(`${date}T00:00:00`)
      );

    const [weekday, value] =
      formatted.split(', ');

    return `${value}, ${weekday}`;
  }

}